import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  QrCode, 
  Eye, 
  TrendingUp, 
  ShoppingCart, 
  Sparkles,
  ArrowRight,
  UtensilsCrossed,
  Download,
  Copy,
  Check,
  Share2
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const copyMenuLink = async () => {
    if (!qrCode?.menu_url) return;
    try {
      await navigator.clipboard.writeText(qrCode.menu_url);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = qrCode.menu_url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    toast.success('Lien du menu copié !', { description: 'Collez-le sur vos réseaux sociaux.' });
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const shareText = encodeURIComponent(`Découvrez le menu de ${restaurant?.name || 'notre restaurant'} 🍽️`);
  const shareUrl = encodeURIComponent(qrCode?.menu_url || '');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, restaurantRes, qrRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/restaurant'),
        api.get('/restaurant/qr-code')
      ]);
      setStats(statsRes.data);
      setRestaurant(restaurantRes.data);
      setQrCode(qrRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiRecommendation = async () => {
    setAiLoading(true);
    try {
      const response = await api.get('/ai/daily-recommendation');
      setAiRecommendation(response.data.recommendation);
    } catch (error) {
      toast.error('Erreur lors du chargement de la recommandation IA');
    } finally {
      setAiLoading(false);
    }
  };

  const downloadQrCode = () => {
    if (!qrCode) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrCode.qr_code}`;
    link.download = `qrcode-${restaurant?.name || 'menu'}.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Scans aujourd'hui",
      value: stats?.scans?.today || 0,
      subtext: `${stats?.scans?.week || 0} cette semaine`,
      icon: <QrCode className="h-5 w-5" />,
      color: "text-orange-500 bg-orange-50"
    },
    {
      title: "Plat le plus vu",
      value: stats?.most_viewed_dish?.name || "—",
      subtext: `${stats?.most_viewed_dish?.views || 0} vues`,
      icon: <Eye className="h-5 w-5" />,
      color: "text-blue-500 bg-blue-50"
    },
    {
      title: "Plat le plus rentable",
      value: stats?.most_profitable_dish?.name || "—",
      subtext: `${stats?.most_profitable_dish?.price || 0}€`,
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-emerald-500 bg-emerald-50"
    },
    {
      title: "Panier estimé",
      value: `${stats?.estimated_basket || 0}€`,
      subtext: `${stats?.total_dishes || 0} plats au menu`,
      icon: <ShoppingCart className="h-5 w-5" />,
      color: "text-purple-500 bg-purple-50"
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
            Bonjour, {restaurant?.name} 👋
          </h1>
          <p className="text-gray-500 mt-1">Voici le résumé de votre activité</p>
        </div>
        <Link to="/menu">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="add-dish-btn">
            <UtensilsCrossed className="mr-2 h-4 w-4" />
            Gérer le menu
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-0 shadow-soft hover:shadow-hover transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-xl font-bold text-gray-900 truncate max-w-[150px]">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.subtext}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${stat.color}`}>
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* QR Code Card */}
        <Card className="border-0 shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <QrCode className="h-5 w-5 text-orange-500" />
              Votre QR Code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {qrCode && (
              <>
                <div className="flex justify-center p-4 bg-white rounded-xl border border-gray-100">
                  <img 
                    src={`data:image/png;base64,${qrCode.qr_code}`} 
                    alt="QR Code Menu"
                    className="w-40 h-40"
                  />
                </div>
                <div className="space-y-2">
                  <button
                    onClick={copyMenuLink}
                    className="w-full flex items-center gap-2 bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-300 rounded-xl px-3 py-2.5 transition-colors group"
                    title="Copier le lien du menu"
                    data-testid="copy-menu-link"
                  >
                    <span className="flex-1 text-xs text-gray-600 truncate text-left">{qrCode.menu_url}</span>
                    {linkCopied ? (
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-400 group-hover:text-orange-500 flex-shrink-0" />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                      <Share2 className="h-3 w-3" /> Partager :
                    </span>
                    <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer"
                      className="flex-1 text-center text-xs font-medium py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                      data-testid="share-whatsapp">
                      WhatsApp
                    </a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                      className="flex-1 text-center text-xs font-medium py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                      data-testid="share-facebook">
                      Facebook
                    </a>
                    <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer"
                      className="flex-1 text-center text-xs font-medium py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      data-testid="share-x">
                      X
                    </a>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 h-10"
                      onClick={downloadQrCode}
                      data-testid="download-qr"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Télécharger
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 h-10"
                      onClick={() => window.open(qrCode.menu_url, '_blank')}
                      data-testid="preview-menu"
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Prévisualiser
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* AI Recommendation Card */}
        <Card className="lg:col-span-2 border-0 shadow-soft bg-gradient-to-br from-orange-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" />
              Recommandation IA du jour
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {aiRecommendation ? (
              <div className="p-4 bg-white rounded-xl border border-orange-100">
                <p className="text-gray-700 leading-relaxed">{aiRecommendation}</p>
              </div>
            ) : (
              <div className="p-4 bg-white rounded-xl border border-orange-100 text-center">
                <p className="text-gray-500 mb-4">
                  Obtenez une recommandation personnalisée de l'IA pour augmenter vos ventes aujourd'hui.
                </p>
                <Button 
                  onClick={fetchAiRecommendation}
                  disabled={aiLoading}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
                  data-testid="get-ai-recommendation"
                >
                  {aiLoading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Obtenir ma recommandation
                    </>
                  )}
                </Button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Link to="/ai">
                <Button variant="outline" className="rounded-full text-sm h-9" data-testid="go-to-ai">
                  Voir l'assistant IA
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/analytics">
                <Button variant="outline" className="rounded-full text-sm h-9">
                  Voir les analytics
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/menu" className="block">
              <div className="p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer">
                <UtensilsCrossed className="h-6 w-6 text-orange-500 mb-2" />
                <p className="font-medium text-gray-900">Ajouter un plat</p>
                <p className="text-sm text-gray-500">Enrichissez votre menu</p>
              </div>
            </Link>
            <Link to="/ai" className="block">
              <div className="p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer">
                <Sparkles className="h-6 w-6 text-orange-500 mb-2" />
                <p className="font-medium text-gray-900">Générer une description</p>
                <p className="text-sm text-gray-500">Avec l'IA</p>
              </div>
            </Link>
            <Link to="/analytics" className="block">
              <div className="p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer">
                <TrendingUp className="h-6 w-6 text-orange-500 mb-2" />
                <p className="font-medium text-gray-900">Voir les stats</p>
                <p className="text-sm text-gray-500">Analysez vos performances</p>
              </div>
            </Link>
            <Link to="/settings" className="block">
              <div className="p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors cursor-pointer">
                <QrCode className="h-6 w-6 text-orange-500 mb-2" />
                <p className="font-medium text-gray-900">Personnaliser</p>
                <p className="text-sm text-gray-500">Logo, couleurs, horaires</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
